import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CoinTransactionDto, WalletDto } from '@cardhub/protocol';
import { api } from '../api/client';
import { Coins, Empty, ErrorState, Loading, Segmented, TopBar } from '../components/ui';
import { useFormat, useT } from '../i18n';
import { errorMessage, useAsync } from '../lib/hooks';

interface PurchaseItem {
  id: string;
  name: string;
  kind: 'COINS' | 'COSMETIC';
  coins: number;
  priceMicros: number;
  currency: string;
  platform: string;
  reference: string | null;
  status: 'PENDING' | 'VERIFIED' | 'CREDITED' | 'FAILED' | 'REFUNDED';
  createdAt: string;
}
const PROVIDER: Record<string, string> = { paypal: 'PayPal', google_play: 'Google Play', app_store: 'App Store', sandbox: 'Test payment' };
const STATUS: Record<PurchaseItem['status'], { label: string; cls: string }> = {
  CREDITED: { label: 'Completed', cls: 'accent' },
  VERIFIED: { label: 'Processing', cls: '' },
  PENDING: { label: 'Pending', cls: '' },
  FAILED: { label: 'Failed', cls: '' },
  REFUNDED: { label: 'Refunded', cls: '' },
};

/** Every real-money purchase: what was bought, how much it cost, how it was paid and its outcome. */
function PurchaseHistory() {
  const f = useFormat();
  const [items, setItems] = useState<PurchaseItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = async (c?: string | null) => {
    setLoading(true);
    try {
      const r = await api.get<{ items: PurchaseItem[]; nextCursor: string | null }>(`/api/v1/store/purchases?limit=25${c ? `&cursor=${encodeURIComponent(c)}` : ''}`);
      setItems((x) => (c ? [...x, ...r.items] : r.items));
      setCursor(r.nextCursor);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const money = (p: PurchaseItem) => new Intl.NumberFormat(undefined, { style: 'currency', currency: p.currency }).format(p.priceMicros / 1e6);
  return (
    <>
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!loading && items.length === 0 && !error && <Empty emoji="🧾" title="No purchases yet" />}
      <div className="list">
        {items.map((p) => (
          <div key={p.id} className="list-item">
            <span className="grow">
              <strong>{p.name}</strong>
              <br />
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                {f.date(p.createdAt)} · {f.time(p.createdAt)} · {PROVIDER[p.platform] ?? p.platform}
              </span>
              {p.reference && (
                <>
                  <br />
                  <span className="muted num" style={{ fontSize: 'var(--fs-xs)' }}>Ref {p.reference}</span>
                </>
              )}
            </span>
            <span className="col" style={{ alignItems: 'flex-end', gap: 4 }}>
              <strong>{money(p)}</strong>
              <span className={`pill ${STATUS[p.status].cls}`}>{STATUS[p.status].label}</span>
            </span>
          </div>
        ))}
      </div>
      {loading && <Loading />}
      {cursor && !loading && (
        <button className="btn ghost block" style={{ marginTop: 12 }} onClick={() => void load(cursor)}>
          Load more
        </button>
      )}
    </>
  );
}

export function Wallet() {
  const t = useT();
  const f = useFormat();
  const nav = useNavigate();
  const wallet = useAsync(() => api.get<WalletDto>('/api/v1/wallet'), []);
  const [tab, setTab] = useState<'coins' | 'purchases'>('coins');
  const [items, setItems] = useState<CoinTransactionDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const load = async (c?: string | null) => {
    setLoading(true);
    try {
      const r = await api.get<{ items: CoinTransactionDto[]; nextCursor: string | null }>(`/api/v1/wallet/transactions?limit=25${c ? `&cursor=${encodeURIComponent(c)}` : ''}`);
      setItems((x) => (c ? [...x, ...r.items] : r.items));
      setCursor(r.nextCursor);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  return (
    <main className="page">
      <TopBar title={t('profile.wallet')} onBack={() => nav(-1)} />
      {wallet.data && (
        <div className="card spread" style={{ marginBottom: 16 }}>
          <Coins amount={wallet.data.balance} size={28} />
          <span className="dim" style={{ fontSize: 'var(--fs-sm)', textAlign: 'right' }}>
            Earned {f.compact(wallet.data.lifetimeEarned)}
            <br />
            Spent {f.compact(wallet.data.lifetimeSpent)}
          </span>
        </div>
      )}
      <Segmented label="History" value={tab} onChange={setTab} options={[{ value: 'coins', label: 'Coin history' }, { value: 'purchases', label: 'Purchases' }]} />
      <div style={{ height: 12 }} />
      {tab === 'purchases' && <PurchaseHistory />}
      {tab === 'coins' && error && <ErrorState message={error} onRetry={() => void load()} />}
      {tab === 'coins' && !loading && items.length === 0 && !error && <Empty emoji="🧾" title="No transactions yet" />}
      <div className="list">
        {tab === 'coins' && items.map((tx) => (
          <div key={tx.id} className="list-item">
            <span className="grow">
              <strong>{tx.description}</strong>
              <br />
              <span className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{f.date(tx.createdAt)} · {f.time(tx.createdAt)}</span>
            </span>
            <span className="col" style={{ alignItems: 'flex-end', gap: 0 }}>
              <Coins amount={tx.amount} signed size={16} />
              <span className="muted num" style={{ fontSize: 'var(--fs-xs)' }}>{f.num(tx.balanceAfter)}</span>
            </span>
          </div>
        ))}
      </div>
      {tab === 'coins' && loading && <Loading />}
      {tab === 'coins' && cursor && !loading && (
        <button className="btn ghost block" style={{ marginTop: 12 }} onClick={() => void load(cursor)}>
          Load more
        </button>
      )}
    </main>
  );
}
